import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { 
  Users, UserCheck, FileText, AlertTriangle, DollarSign, Smartphone,
  Shield, CheckCircle, ArrowRight, Brain, FileEdit, ClipboardCheck,
  Target, Zap, Clock, Award, Building, Heart, Briefcase, MapPin,
  Star, Phone, Calendar, BarChart, Lock
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster } from 'sonner';
import { useBooking } from '../context/BookingContext';
import ChallengesSection from '../components/sections/ChallengesSection';
import SolutionsGrid from '../components/sections/SolutionsGrid';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SolutionsPage = () => {
  const { openBooking, openContact } = useBooking();
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);

  const iconMap = {
    Building, Heart, Target, DollarSign, Award, Briefcase, Users, Shield, Clock, FileText,
    UserCheck, AlertTriangle, Smartphone, Brain, FileEdit, ClipboardCheck, Zap, MapPin,
    Star, Phone, Calendar, BarChart, Lock
  };

  useEffect(() => {
    const fetchSolutions = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/solutions`);
        const data = await res.json();
        setSolutions(data || []);
      } catch (e) {
        console.error('Failed to fetch solutions:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchSolutions();
  }, []);

  const australianFeatures = [
    { icon: MapPin, title: 'Australian Hosted', desc: 'Data stays in Australia', color: 'care-teal' },
    { icon: Shield, title: 'NDIS Compliant', desc: 'Quality & Safeguards ready', color: 'tech-blue' },
    { icon: Lock, title: 'Privacy Act', desc: 'Australian privacy laws', color: 'heritage-orange' },
    { icon: Phone, title: 'Local Support', desc: 'Australian-based team', color: 'purple-500' }
  ];

  const stats = [
    { value: '500+', label: 'Australian Providers' },
    { value: '20K+', label: 'Participants Served' },
    { value: '99.8%', label: 'Compliance Rate' },
    { value: '24/7', label: 'Australian Support' }
  ];

  return (
    <div className="min-h-screen bg-page-bg">
      <SEOHead 
        title="Solutions | GetCare 360 - Custom NDIS Software for Australian Providers"
        description="Discover tailored NDIS software solutions for your specific needs. SIL, SDA, Plan Management, Support Coordination - GetCare 360 builds custom platforms for every provider type."
        keywords="NDIS software solutions, SIL provider software, SDA software, plan management software, support coordination platform Australia"
        canonicalUrl="/solutions"
        page="solutions"
      />
      <Toaster position="top-right" richColors />
      <Header />

      {/* Hero Section - Australian NDIS Focus */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-primary-navy via-[#0d2847] to-primary-navy relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-care-teal rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-tech-blue rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
                <span className="text-2xl mr-2">🇦🇺</span>
                <span className="text-sm font-medium text-white">Built for Australian NDIS Providers</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Custom Solutions for{' '}
                <span className="text-care-teal">Every Australian</span>{' '}
                <span className="text-tech-blue">NDIS Provider</span>
              </h1>
              
              <p className="text-xl text-white/80 mb-8 leading-relaxed">
                We don't believe in one-size-fits-all. GetCare is custom-built for your specific 
                NDIS operational model—whether you're in Sydney, Melbourne, Brisbane, Perth, or regional Australia.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <Button 
                  onClick={openBooking}
                  className="bg-care-teal hover:bg-care-teal/90 text-white px-8 py-6 text-lg rounded-xl"
                >
                  Book a Demo
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button onClick={openContact} variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl">
                    Contact Us
                  </Button>
              </div>

              {/* Australian Features */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {australianFeatures.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                      <Icon className={`text-${feature.color} mb-2`} size={20} />
                      <div className="text-white text-sm font-semibold">{feature.title}</div>
                      <div className="text-white/60 text-xs">{feature.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/7551622/pexels-photo-7551622.jpeg?auto=compress&cs=tinysrgb&w=1200"
                  alt="Australian NDIS Care Provider helping participant"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-navy/60 to-transparent"></div>
                
                {/* Stats Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="grid grid-cols-4 gap-2">
                    {stats.map((stat, index) => (
                      <div key={index} className="text-center">
                        <div className="text-2xl font-bold text-white">{stat.value}</div>
                        <div className="text-xs text-white/70">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -top-4 -right-4 bg-care-teal text-white px-4 py-2 rounded-xl shadow-lg">
                <div className="flex items-center space-x-2">
                  <Star className="fill-white" size={16} />
                  <span className="font-semibold text-sm">Trusted Across Australia</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Challenge - NEW INTERACTIVE SECTION */}
      <ChallengesSection />

      {/* Solutions by Provider Type - NEW INTERACTIVE SECTION */}
      <SolutionsGrid solutions={solutions} openBooking={openBooking} />

      {/* Why Australian Providers Choose GetCare */}
      <section className="py-20 bg-primary-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-6">
                <span className="text-2xl mr-2">🇦🇺</span>
                <span className="text-sm font-medium">Australian Made & Supported</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why Australian NDIS Providers{' '}
                <span className="text-care-teal">Choose GetCare</span>
              </h2>
              
              <p className="text-xl text-white/80 mb-8">
                GetCare is not a template system. We build a custom AI-powered platform tailored to your NDIS operational model—designed and supported right here in Australia.
              </p>
              
              <div className="space-y-4">
                {[
                  'Data hosted on Australian servers (AWS Sydney)',
                  'Compliant with Australian Privacy Principles',
                  'NDIS Worker Screening integration',
                  'Australian payroll & award interpretation (SCHADS)',
                  'Local Australian support team',
                  'Designed for Australian healthcare workflows'
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="text-care-teal flex-shrink-0" size={20} />
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8">
                <Button 
                  onClick={openBooking}
                  className="bg-care-teal hover:bg-care-teal/90 text-white px-8 py-6 text-lg"
                >
                  Book a Strategy Call
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/8127501/pexels-photo-8127501.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Australian NDIS Support Worker with Participant"
                className="rounded-2xl shadow-2xl"
              />
              
              {/* Map Badge */}
              <div className="absolute -bottom-6 -left-6 bg-surface p-4 rounded-xl shadow-xl">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-care-teal/10 flex items-center justify-center">
                    <MapPin className="text-care-teal" size={20} />
                  </div>
                  <div>
                    <div className="font-bold text-text-primary text-sm">All of Australia</div>
                    <div className="text-xs text-text-secondary">Metro & Regional</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-tech-blue to-primary-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-6">
            <Calendar className="text-white mr-2" size={18} />
            <span className="text-sm font-medium text-white">Free Strategy Session</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your{' '}
            <span className="text-care-teal">NDIS Operations?</span>
          </h2>
          
          <p className="text-xl text-white/80 mb-8">
            Book a demo or contact us to learn how we can help your NDIS provider.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={openBooking}
              className="bg-white text-primary-navy hover:bg-white/90 px-8 py-6 text-lg"
            >
              Book a Demo
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button onClick={openContact} variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Us
              </Button>
          </div>
          
          <p className="text-white/60 text-sm mt-6">
            100% Australian owned and operated
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SolutionsPage;
